import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { GatewayBrowserClient, type GatewayEventFrame, type GatewayHelloOk } from "../gateway";

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "error";

export type GatewayContextValue = {
  client: GatewayBrowserClient | null;
  status: ConnectionStatus;
  hello: GatewayHelloOk | null;
  lastEvent: GatewayEventFrame | null;
  request: <T = unknown>(method: string, params?: unknown) => Promise<T>;
  /** Subscribe to every gateway event. Returns an unsubscribe function. */
  subscribe: (fn: (evt: GatewayEventFrame) => void) => () => void;
};

const GatewayContext = createContext<GatewayContextValue | null>(null);

export type GatewayProviderProps = {
  url: string;
  token?: string;
  clerkJwt?: string;
  resolveClerkJwt?: () => Promise<string | undefined> | string | undefined;
  orgId?: string;
  requireClerkJwt?: boolean;
  children: ReactNode;
};

export function GatewayProvider({
  url,
  token,
  clerkJwt,
  resolveClerkJwt,
  orgId,
  requireClerkJwt = false,
  children,
}: GatewayProviderProps) {
  const [status, setStatus] = useState<ConnectionStatus>("disconnected");
  const [hello, setHello] = useState<GatewayHelloOk | null>(null);
  const [lastEvent, setLastEvent] = useState<GatewayEventFrame | null>(null);
  const clientRef = useRef<GatewayBrowserClient | null>(null);
  const subscribersRef = useRef<Set<(evt: GatewayEventFrame) => void>>(new Set());

  /*
   * SYN-2530 — THE CONNECTION MUST NOT BE REBUILT BY THINGS THAT ARE NOT THE CONNECTION.
   *
   * ⚠️ READ THIS FIRST: THIS IS NOT THE CAUSE OF THE LOST CONNECT REPLY. I claimed it was, and
   * the gateway's own timestamps refuted me. Keeping the refutation here so nobody re-derives the
   * wrong conclusion from the same log:
   *
   *   21:46:44.920  [orchestrator] Agent: Command (command)      <- run starts
   *   21:46:49.321  [agent-tool]   START connect_my_system
   *   21:46:53.321  [agent-tool]   START connect_my_system
   *   21:46:57.046  [agent-run]    prompt() resolved — 33 events, finalText: 384 chars
   *   21:47:31.742  [gateway-v2]   Client disconnected            <- 34s LATER
   *
   * No disconnect lands between the run starting and the reply resolving. The socket was up the
   * whole time, so the reply was NOT lost to a teardown. The browser rendered nothing for some
   * other reason, and that reason is still open.
   *
   * ✅ WHAT IS MEASURED AND IS A REAL DEFECT: the socket churns hard around mount. Ten
   * connect/disconnect events inside ONE SECOND, 21:46:42.314 → 21:46:43.027, before the run
   * began. `clerkJwt` was in this effect's dependency array and `resolveClerkJwt` was a
   * `useCallback` keyed on Clerk's `getToken`, so a token arriving, an org id resolving, or
   * Clerk rebuilding its auth object each destroyed a working connection and built a new one.
   * A turn that straddles any of those loses its stream, which is a real way to lose a real
   * answer even though it is not how THIS answer was lost.
   *
   * ⇒ So the token is read through a ref, and the effect depends on WHETHER we have one, not on
   * WHICH one it is. `false → true` still opens the connection; `true → true` no longer closes it.
   *
   * 🔑 SAFE BECAUSE THE CLIENT ALREADY REFRESHES ITSELF. `GatewayBrowserClient.connect()` calls
   * `resolveClerkJwt()` on every attempt and prefers its result over this seed value
   * (`gateway/client.ts:173-180`), and it owns its own reconnect with backoff (`:104`,
   * `:111-117`). This effect was never a source of freshness. It only ever destroyed a working
   * connection to hand it a token the client would have fetched anyway.
   *
   * ⚠️ WHAT STILL REBUILDS THE SOCKET, deliberately: a different `url`, a different `orgId`, or
   * `requireClerkJwt` flipping. Those change WHO or WHERE the connection is for, and reusing a
   * socket across an org switch would leak one tenant's stream into another's session.
   */
  const clerkJwtRef = useRef(clerkJwt);
  clerkJwtRef.current = clerkJwt;
  const resolveClerkJwtRef = useRef(resolveClerkJwt);
  resolveClerkJwtRef.current = resolveClerkJwt;

  /** Identity-stable wrapper, so a new `resolveClerkJwt` callback cannot retrigger the effect. */
  const resolveLatestClerkJwt = useCallback(() => resolveClerkJwtRef.current?.(), []);

  /** Whether a token exists at all — the gate — as opposed to which token it is. */
  const hasClerkJwt = Boolean(clerkJwt);

  useEffect(() => {
    if (!url) {
      return;
    }
    if (requireClerkJwt && !hasClerkJwt) {
      return;
    }
    if (requireClerkJwt && !orgId?.trim()) {
      return;
    }
    if (!token && !hasClerkJwt && !resolveClerkJwtRef.current) {
      return;
    }

    setStatus("connecting");
    const client = new GatewayBrowserClient({
      url,
      token,
      clerkJwt: clerkJwtRef.current,
      resolveClerkJwt: resolveLatestClerkJwt,
      orgId,
      onHello: (h) => {
        setHello(h);
        setStatus("connected");
      },
      onEvent: (evt) => {
        setLastEvent(evt);
        // Notify subscribers synchronously so no events are lost to React batching.
        for (const fn of subscribersRef.current) {
          try {
            fn(evt);
          } catch {
            // Subscriber errors must not break event dispatch.
          }
        }
      },
      onClose: () => {
        setStatus("disconnected");
      },
    });

    clientRef.current = client;
    client.start();

    return () => {
      client.stop();
      clientRef.current = null;
    };
  }, [url, token, hasClerkJwt, resolveLatestClerkJwt, orgId, requireClerkJwt]);

  // Stable reference: clientRef doesn't change identity, so no deps needed
  const request = useCallback(async <T = unknown>(method: string, params?: unknown): Promise<T> => {
    const client = clientRef.current;
    if (!client) {
      throw new Error("Gateway not connected");
    }
    return client.request<T>(method, params);
  }, []);

  const subscribe = useCallback((fn: (evt: GatewayEventFrame) => void) => {
    subscribersRef.current.add(fn);
    return () => {
      subscribersRef.current.delete(fn);
    };
  }, []);

  const value = useMemo<GatewayContextValue>(
    () => ({ client: clientRef.current, status, hello, lastEvent, request, subscribe }),
    [status, hello, lastEvent, request, subscribe],
  );

  return <GatewayContext.Provider value={value}>{children}</GatewayContext.Provider>;
}

export function useGateway(): GatewayContextValue {
  const ctx = useContext(GatewayContext);
  if (!ctx) {
    throw new Error("useGateway must be used within a GatewayProvider");
  }
  return ctx;
}
